/**
 * Emotion Cache for SSR Hydration
 *
 * Creates an Emotion cache with a consistent insertion point for SSR.
 *
 * ## How It Works
 *
 * 1. `_document.tsx` creates a cache for SSR and extracts critical CSS
 * 2. CSS is injected as `<style data-emotion="chakra ...">` tags
 * 3. `_app.tsx` creates a client cache with the same key
 * 4. SSR styles persist in DOM; client adds new styles as needed
 *
 * ## Note on "Flash" Issues
 *
 * If you see layout shifts or style flashing, it's likely NOT from Emotion
 * regenerating styles. More likely causes:
 * - `useMediaQuery` SSR mismatch (returns false on server, true on client)
 * - `styled()` components with dynamic values
 * - React hydration timing
 *
 * The fix for those issues is to use CSS-based responsive design
 * (Chakra responsive props) instead of JS-based media queries.
 *
 * @see https://emotion.sh/docs/ssr#nextjs
 */
import createCache, { EmotionCache } from "@emotion/cache";

const isBrowser = typeof document !== "undefined";

// Client-side singleton cache
let clientCache: EmotionCache | null = null;

/**
 * Creates an Emotion cache with a consistent insertion point.
 *
 * The insertion point is a `<meta name="emotion-insertion-point">` tag
 * defined in `_document.tsx`. This ensures styles are inserted
 * in a predictable location in the `<head>`.
 */
export default function createEmotionCache(): EmotionCache {
  let insertionPoint: HTMLElement | undefined;

  if (isBrowser) {
    const emotionInsertionPoint = document.querySelector<HTMLMetaElement>(
      'meta[name="emotion-insertion-point"]'
    );
    insertionPoint = emotionInsertionPoint ?? undefined;
  }

  return createCache({ key: "chakra", insertionPoint });
}

/**
 * Gets the client-side Emotion cache singleton.
 *
 * Using a singleton ensures we don't create multiple caches
 * during client-side navigation.
 */
export function getClientEmotionCache(): EmotionCache {
  if (!isBrowser) {
    return createEmotionCache();
  }

  if (!clientCache) {
    clientCache = createEmotionCache();
  }

  return clientCache;
}
